<template>
  <div>
    <div>
      <h5 class="inline">
        {{ heading }}
      </h5>
      <Tooltip direction="top" :contents="tooltip" class="inline">
        <IconInformationCircle size="md" weight="fill" class="text-primary" />
      </Tooltip>
      <SourceOfCurationTooltip :sourceOfCuration="overviewFieldSource" />
      <HistoryButton :keyValue="keyValue" :entity="entity" :heading="heading" />
      <div
        v-if="isEditVisible"
        class="inline pull-right"
        style="margin-right: 0px"
      >
        <button
          v-if="userManager.userData.app_metadata.canonical_id && !editMode && !this.isObjectReadOnly "
          class="btn-icon transparent"
          @click="startEditing"
        >
          <Tooltip direction="top" contents="Edit">
            <i class="fa fa-pencil" aria-hidden="true" />
          </Tooltip>
        </button>
      </div>
      <!-- Add Slack Channel or Squad Email -->
      <div v-if="isEditVisible">
        <div v-if="isLoading && userInput" class="loading" style="padding: 10px 10px 10px 0;">
          <Spinner :width="25" />
          Updating...
        </div>
        <span
          v-else-if="userManager.userData.app_metadata.canonical_id && editMode"
          class="col-xs-12"
          style="padding: 0; margin: 0;"
          @keyup.enter="userInput !== '' ? (confirmAdd = true) : ''"
        >
          <div
            style="padding: 0; margin: 0; width: 80%;"
            class="pull-left form-group"
            :class="[!suggestedOptions.length && userInput ? 'form-group-active' : '', errorMessage ? 'has-error' : '']"
          >
            <label v-if="!suggestedOptions.length" class="control-label" :title="heading">{{ heading }}</label>
            <Select
              v-if="suggestedOptions.length"
              :value="userInput ? { label: userInput, value: userInput } : null"
              :options="suggestedOptions.map(option => ({ label: option, value: option }))"
              :isClearable="true"
              :isDisabled="isLoading"
              :placeholder="heading"
              @onInputChange="handleInputChange"
              @onChange="selectSuggestion"
            />
            <input
              v-else
              v-model="userInput"
              :type="isSingleValue ? 'url' : 'text'"
              class="form-control"
              @click="(e) => { userInput = e.target.value; errorMessage = false}"
            />
            <small v-if="errorMessage && errorMessage != ''" class="help-block">{{ errorMessage }}</small>
            <br />
          </div>
          <div class="inline-edit-group pull-right" style="padding: 0; margin: 0; min-width: 20%; width: 20%;">
            <div class="right-addon">
              <button title="Add" @click="confirmAdd = false; updateFieldValue(userInput);">
                <i class="fa fa-check" style="margin: 0;" />
              </button>
              <button title="cancel">
                <i class="fa fa-times" style="margin: 0;" @click="editMode = !editMode; errorMessage = null;" />
              </button>
            </div>
          </div>
        </span>
      </div>
      <div v-if="programmaticFieldList.length === 0 && !editMode">
        <span class="text-muted" style="padding-left: 0px;">
          No
          {{ entity === 'slack_channel' ? 'slack channel' : (isSingleValue ? 'value' : 'data owner') }}
          added
        </span>
      </div>
      <!-- functionality for viewing slack_channel and squad_email fields data -->
      <span v-if="programmaticFieldList" style="padding: 0px">
        <div
          v-for="programmaticFieldData in programmaticFieldList"
          :key="programmaticFieldData"
          style="margin-bottom: 5px"
        >
          <div
            v-if="isLoading && valueToDelete === programmaticFieldData"
            class="loading"
            align="left"
          >
            <Spinner :width="25" />
          </div>
          <div v-else>
            <button
              v-if="editMode && isUserSteward && !isSingleValue"
              class="pull-right btn btn-own transparent"
              @click="
                confirmDelete = true;
                valueToDelete = programmaticFieldData;
              "
            >
              <i v-if="!isLoading" class="fa fa-trash" aria-hidden="true" />
            </button>
            <button v-if="entity === 'squad_email'" class="owner-btn btn btn-outline-primary">
              {{ programmaticFieldData.charAt(0).toUpperCase() }}
            </button>
            <a
              v-if="entity === 'git_repository_url'"
              :href="programmaticFieldData"
              target="_blank"
            >
              {{ programmaticFieldData }}</a>
            <div v-if="entity === 'transformation_tool'">
              {{ programmaticFieldData }}
            </div>
            <a
              v-if="entity === 'slack_channel'"
              :href="`${getSlackWorkspace()}/app_redirect?channel=${programmaticFieldData.slice(
                1
              )}`"
              target="_blank"
            >
              {{ programmaticFieldData }}</a>
            <a
              v-if="entity === 'squad_email'"
              :href="`mailto:${programmaticFieldData}`"
              target="_blank"
            >
              {{ programmaticFieldData }}</a>
            <ConfirmBox
              v-if="confirmDelete"
              :title="`Remove ${heading}`"
              :message="warningMessageDelete"
              @confirmOperation="
                confirmDeleteOperation();
              "
              @closeModal="confirmDelete = false"
            />
          </div>
        </div>
      </span>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { Tooltip } from '@cimpress/react-components';
import { IconInformationCircle } from '@cimpress-technology/react-streamline-icons/lib';
import Select from 'react-select';
import { clusterToSlackWorkspace } from '../../../utils/slackWorkspaceMapping';
import tableKeyToCluster from '../../../utils/tableKeyToCluster';
import ConfirmBox from '../../ConfirmBox';
import SourceOfCurationTooltip from '../SourceOfCurationTooltip';
import HistoryButton from './HistoryButton.vue';
import Spinner from '../../Icons/Spinner.vue';

export default {
  name: 'OverviewFields',
  inject: ['userManager', 'metadataPlatformClient'],
  components: {
    ConfirmBox,
    Tooltip,
    IconInformationCircle,
    Select,
    SourceOfCurationTooltip,
    HistoryButton,
    Spinner
  },
  props: {
    keyValue: {
      type: String,
      default: () => ''
    },
    objectType: {
      type: String,
      required: true
    },
    heading: {
      type: String,
      default: () => ''
    },
    entity: {
      type: String,
      default: () => ''
    },
    tooltip: {
      type: String,
      default: () => ''
    },
    isUserSteward: {
      type: Boolean,
      required: true
    },
    isStable: {
      type: Boolean,
      default: () => false
    },
    objectCluster: {
      type: String,
      required: false,
      default: () => 'cimpress.eu-west-1'
    },
    overviewFieldSource: {
      type: String,
      default: () => null
    },
    isSingleValue: {
      type: Boolean,
      default: () => false
    },
    suggestedOptions: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      confirmAdd: false,
      confirmDelete: false,
      isHistoryOn: false,
      editMode: false,
      userInput: '',
      valueToDelete: '',
      userInputError: false,
      programmaticFieldList: [],
      isLoading: false,
      errorMessage: null
    };
  },
  computed: {
    ...mapState({
      isObjectReadOnly: state => state.tables.isObjectReadOnly
    }),
    customFields() {
      return this.$store.state[`${this.objectType}s`].customFields;
    },
    warningMessageAdd() {
      return `Are you sure you want to add <b>${this.userInput}</b> to ${this.heading} list for this Data object?`;
    },
    warningMessageDelete() {
      const message = `Are you sure you want to remove <b>${this.valueToDelete}</b> from ${this.heading}s list?`;
      return message;
    },
    isEditVisible() {
      return this.isUserSteward && this.overviewFieldSource !== 'data_portal_api';
    }
  },
  watch: {
    userInput() {
      this.userInputError = false;
    }
  },
  created() {
    const fieldValue = this.customFields[this.entity];
    this.programmaticFieldList = (Array.isArray(fieldValue) ? fieldValue : [fieldValue])
      .filter(value => value)
      .sort();
  },
  methods: {
    ...mapActions({
      putSlackChannel(dispatch, p) {
        return dispatch(`${this.objectType}s/putSlackChannel`, p);
      },
      putSquadEmail(dispatch, p) {
        return dispatch(`${this.objectType}s/putSquadEmail`, p);
      },
      putGitRepositoryUrl(dispatch, p) {
        return dispatch(`${this.objectType}s/putGitRepositoryUrl`, p);
      },
      putTransformationTool(dispatch, p) {
        return dispatch(`${this.objectType}s/putTransformationTool`, p);
      }
    }),
    // eslint-disable-next-line require-await
    async confirmDeleteOperation() {
      this.confirmDelete = false;
      this.deleteFieldValue();
    },
    async deleteFieldValue() {
      try {
        this.errorMessage = null;
        this.isLoading = true;
        let updateData = [...this.programmaticFieldList];
        updateData = updateData.filter(val => val !== this.valueToDelete);

        if (this.entity === 'slack_channel') {
          await this.putSlackChannel({
            platformClient: this.metadataPlatformClient,
            keyValue: this.keyValue,
            slackChannel: updateData
          });
        } else if (this.entity === 'squad_email') {
          await this.putSquadEmail({
            platformClient: this.metadataPlatformClient,
            keyValue: this.keyValue,
            squadEmail: updateData
          });
        }
        this.programmaticFieldList = this.getProgrammaticFieldList();
        this.valueToDelete = '';
      } catch (e) {
        this.errorMessage
          = e.message
          || e.data.message
          || e.data.title
          || (e.status && `Error occurred with status code ${e.status}`)
          || 'Unknown error occurred';
        console.log(e);
      }
      this.isLoading = false;
    },
    async updateFieldValue(inputValue) {
      try {
        this.errorMessage = null;
        this.isLoading = true;
        const trimmedValue = this.isSingleValue ? inputValue.trim() : inputValue;
        const updateData = this.isSingleValue
          ? trimmedValue
          : [...this.programmaticFieldList, inputValue];
        if (this.entity === 'git_repository_url') {
          await this.putGitRepositoryUrl({
            platformClient: this.metadataPlatformClient,
            keyValue: this.keyValue,
            gitRepositoryUrl: updateData
          });
        } else if (this.entity === 'transformation_tool') {
          await this.putTransformationTool({
            platformClient: this.metadataPlatformClient,
            keyValue: this.keyValue,
            transformationTool: updateData
          });
        } else if (this.entity === 'slack_channel') {
          await this.putSlackChannel({
            platformClient: this.metadataPlatformClient,
            keyValue: this.keyValue,
            slackChannel: updateData
          });
        } else if (this.entity === 'squad_email') {
          await this.putSquadEmail({
            platformClient: this.metadataPlatformClient,
            keyValue: this.keyValue,
            squadEmail: updateData
          });
        }
        this.programmaticFieldList = this.isSingleValue
          ? [trimmedValue]
          : (this.entity === 'slack_channel'
            ? updateData.map(value => value.charAt(0) === '#' ? value : `#${value}`)
            : [...updateData]);
        this.userInput = this.isSingleValue ? trimmedValue : '';
      } catch (e) {
        this.errorMessage
          = e.message
          || e.data.message
          || e.data.title
          || (e.status && `Error occurred with status code ${e.status}`)
          || 'Unknown error occurred';
        console.log(e);
      }
      this.isLoading = false;
    },
    startEditing() {
      this.editMode = true;
      this.isLoading = false;
      this.errorMessage = null;
      this.userInput = this.isSingleValue ? (this.customFields[this.entity] || '') : '';
    },
    handleInputChange(inputValue, actionMeta) {
      if (actionMeta.action === 'input-change') {
        this.userInput = inputValue;
      }
    },
    selectSuggestion(option) {
      this.userInput = option ? option.value : '';
    },
    getProgrammaticFieldList() {
      const fieldValue = this.customFields[this.entity];
      return (Array.isArray(fieldValue) ? fieldValue : [fieldValue])
        .filter(value => value)
        .sort();
    },
    getSlackWorkspace() {
      if (this.isObjectReadOnly) {
        // If the object is read-only, it means it is a consumer object and we need to get the workspace from the producer object
        return clusterToSlackWorkspace[tableKeyToCluster(this.customFields.producer_object_key)];
      }
      return clusterToSlackWorkspace[this.objectCluster];
    }
  }
};
</script>
<style scoped>
.btn-own {
  margin: 0px;
  padding: 0px;
  color: #cb4154;
}
span {
  padding: 0px 5px 0px 11px;
}
</style>
